import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { classNumberLabel, sortByClassNumber } from "../util/classDisplay";

// #3516 - "Put this score on its real ride". A scribe saved a score for a ride
// that does not exist (the tablet built a ride id the show never had). The
// server proposes the ride it most likely belongs to (same entry number, same
// test); this dialog shows that proposal pre-selected and lets staff pick a
// different ride in the same show. If the test on the score differs from the
// test on the chosen ride, staff must tick a box before the score is attached
// (the server enforces the same rule and answers 409 test_mismatch).
//
// Plain staff words only: no ids, no "orphan", no "attach to rideId".
const rideIdOf = (r) => r?.id ?? r?.rideId ?? "";
const testNameOf = (test) => test?.name || test?.testCode || null;

// Server refusals, each with its own plain-words text. STATIC i18n keys.
const ATTACH_ERROR_KEY = {
  sheet_not_orphan: "reviewQueue.orphanSheet.errors.sheetNotOrphan",
  target_wrong_show: "reviewQueue.orphanSheet.errors.targetWrongShow",
  target_not_found: "reviewQueue.orphanSheet.errors.targetNotFound",
  concurrent_modification: "reviewQueue.orphanSheet.errors.concurrentModification",
};

export default function AttachOrphanSheetDialog({ open, item, onClose, onAttached }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const subject = item?.subject ?? null;
  const proposed = subject?.proposedRide ?? null;

  const [rides, setRides] = useState([]);
  const [classesById, setClassesById] = useState({});
  const [testsById, setTestsById] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedRideId, setSelectedRideId] = useState("");
  const [confirmMismatch, setConfirmMismatch] = useState(false);
  const [serverMismatch, setServerMismatch] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorKey, setErrorKey] = useState(null);

  const load = useCallback(async () => {
    if (!subject) return;
    setLoading(true);
    setLoadFailed(false);
    try {
      // The row normally carries its show; an older row may not, so the class
      // it points at (or the proposed ride's class) names the show instead.
      let showId = subject.showId;
      if (!showId) {
        const classId = proposed?.classId ?? subject.classId;
        const cls = classId ? await scoreApi.classes.getClass(classId).then(unwrap).catch(() => null) : null;
        showId = cls?.showId ?? null;
      }
      if (!showId) {
        setRides([]);
        return;
      }
      const [allRides, classes, tests] = await Promise.all([
        scoreApi.rides.listRides(undefined, showId).then(unwrap),
        scoreApi.classes.listClassesForShows({ showIds: [showId] }).then(unwrap).catch(() => []),
        scoreApi.tests.listTests(showId).then(unwrap).catch(() => []),
      ]);
      setRides((allRides || []).filter((r) => !r.isManual));
      setClassesById(Object.fromEntries((classes || []).map((c) => [c.id, c])));
      setTestsById(Object.fromEntries((tests || []).map((x) => [x.id, x])));
    } catch {
      setRides([]);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [subject, proposed, scoreApi]);

  useEffect(() => {
    if (!open) return;
    setSelectedClassId(proposed?.classId ?? subject?.classId ?? "");
    setSelectedRideId(proposed?.id ?? "");
    setConfirmMismatch(false);
    setServerMismatch(false);
    setErrorKey(null);
    setSaving(false);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  const classOptions = useMemo(() => {
    const ids = new Set(rides.map((r) => r.classId).filter(Boolean));
    if (selectedClassId) ids.add(selectedClassId);
    return sortByClassNumber([...ids].map((id) => classesById[id] || { id, className: id }));
  }, [rides, classesById, selectedClassId]);

  const classRides = useMemo(() => {
    const timeOf = (r) => {
      const v = r.scheduledTime ? Date.parse(r.scheduledTime) : NaN;
      return Number.isNaN(v) ? Infinity : v;
    };
    return rides
      .filter((r) => r.classId === selectedClassId)
      .sort((a, b) => {
        const ta = timeOf(a);
        const tb = timeOf(b);
        if (ta !== tb) return ta < tb ? -1 : 1;
        return String(rideIdOf(a)).localeCompare(String(rideIdOf(b)));
      });
  }, [rides, selectedClassId]);

  const selectedRide = rides.find((r) => rideIdOf(r) === selectedRideId) ?? null;
  const sheetTestId = subject?.testId ?? null;
  const targetTestId = selectedRide?.testId ?? null;
  const clientMismatch = !!(selectedRide && sheetTestId && targetTestId && sheetTestId !== targetTestId);
  const mismatch = clientMismatch || serverMismatch;

  const testLabel = (id) => (id ? testNameOf(testsById[id]) || t("reviewQueue.orphanSheet.testUnnamed") : t("reviewQueue.attach.noTest"));
  const rideLabel = (r) => {
    const cls = r.classId ? classesById[r.classId] : null;
    return [
      cls ? classNumberLabel(cls) : r.className || t("common.unknownClass"),
      r.competitorNumber != null ? `#${r.competitorNumber}` : null,
      r.riderName || r.horseName || t("common.unknownUser"),
    ]
      .filter(Boolean)
      .join(" - ");
  };

  const handleClassChange = (next) => {
    setSelectedClassId(next);
    setSelectedRideId("");
    setConfirmMismatch(false);
    setServerMismatch(false);
  };

  const handleAttach = async () => {
    setSaving(true);
    setErrorKey(null);
    try {
      const data = unwrap(
        await scoreApi.reviewQueue.attachOrphanSheet(subject.id, {
          targetRideId: selectedRideId,
          ...(mismatch && confirmMismatch ? { confirmTestMismatch: true } : {}),
        }),
      );
      await onAttached?.(data, selectedRide);
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === "test_mismatch") {
        setServerMismatch(true);
        setConfirmMismatch(false);
      } else {
        setErrorKey(ATTACH_ERROR_KEY[code] ?? "reviewQueue.orphanSheet.errors.default");
        // The sheet already left the missing-ride state: the list is stale, so
        // hand back to the page, which closes this and says so on the list.
        if (code === "sheet_not_orphan") await onAttached?.(null, null);
      }
    } finally {
      setSaving(false);
    }
  };

  const judgeText = subject?.judgeName || (subject?.judgeId ? t("reviewQueue.orphanSheet.judgePosition", { position: subject.judgeId }) : t("common.unknownJudge"));
  const marksRows = [
    ...(subject?.marks?.movements ?? []).map((m) => ({
      key: `m${m.movementNumber}`,
      label: t("reviewQueue.orphanSheet.movementMark", { number: m.movementNumber }),
      score: m.score,
    })),
    ...(subject?.marks?.collectives ?? []).map((c) => ({
      key: `c${c.key}`,
      label: t("reviewQueue.orphanSheet.collectiveMark", { key: c.key }),
      score: c.score,
    })),
  ];
  const noRides = !loading && !loadFailed && rides.length === 0;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth data-testid="attach-orphan-sheet-dialog">
      <DialogTitle>{t("reviewQueue.orphanSheet.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" data-testid="orphan-sheet-intro">
            {t("reviewQueue.orphanSheet.intro")}
          </Typography>
          <Box data-testid="orphan-sheet-score">
            <Typography variant="caption" color="text.secondary">
              {t("reviewQueue.orphanSheet.scoreHeading")}
            </Typography>
            <Typography>
              {t("reviewQueue.orphanSheet.scoreLine", {
                judge: judgeText,
                entry: subject?.competitorNumber ?? subject?.entryNumber ?? "-",
                score: typeof subject?.percentage === "number" ? `${subject.percentage.toFixed(3)}%` : "-",
              })}
            </Typography>
            {typeof subject?.totalPoints === "number" ? (
              <Typography variant="body2" data-testid="orphan-sheet-total-points">
                {t("reviewQueue.orphanSheet.totalPointsLine", { points: subject.totalPoints })}
              </Typography>
            ) : null}
          </Box>

          {/* #3515 AC1: read-only, so staff can check it is the right sheet. */}
          <Box data-testid="orphan-sheet-marks">
            <Typography variant="caption" color="text.secondary">
              {t("reviewQueue.orphanSheet.marksHeading")}
            </Typography>
            {marksRows.length === 0 ? (
              <Typography variant="body2">{t("reviewQueue.orphanSheet.noMarks")}</Typography>
            ) : (
              <Box component="table" sx={{ borderCollapse: "collapse", "& td": { py: 0.25, pr: 3 } }}>
                <tbody>
                  {marksRows.map((row) => (
                    <tr key={row.key} data-testid="orphan-sheet-mark-row">
                      <td>{row.label}</td>
                      <td>{row.score ?? t("reviewQueue.orphanSheet.markBlank")}</td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            )}
          </Box>

          {proposed ? (
            <Alert severity="info" data-testid="orphan-sheet-proposed">
              {t("reviewQueue.orphanSheet.proposed", {
                ride: rideLabel(proposed),
              })}
            </Alert>
          ) : (
            <Alert severity="warning" data-testid="orphan-sheet-no-proposal">
              {t("reviewQueue.orphanSheet.noProposal")}
            </Alert>
          )}

          {errorKey && (
            <Alert severity="error" data-testid="orphan-sheet-error">
              {t(errorKey)}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : loadFailed ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={load}>
                  {t("reviewQueue.attach.refresh")}
                </Button>
              }
            >
              {t("reviewQueue.attach.loadError")}
            </Alert>
          ) : noRides ? (
            <Alert severity="info" data-testid="orphan-sheet-no-rides">
              {t("reviewQueue.orphanSheet.noRides")}
            </Alert>
          ) : (
            <>
              <FormControl fullWidth>
                <InputLabel id="orphan-class-label">{t("reviewQueue.attach.classLabel")}</InputLabel>
                <Select
                  labelId="orphan-class-label"
                  label={t("reviewQueue.attach.classLabel")}
                  value={selectedClassId}
                  onChange={(e) => handleClassChange(e.target.value)}
                >
                  {classOptions.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {classNumberLabel(c)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="orphan-ride-label">{t("reviewQueue.orphanSheet.rideLabel")}</InputLabel>
                <Select
                  labelId="orphan-ride-label"
                  label={t("reviewQueue.orphanSheet.rideLabel")}
                  value={classRides.some((r) => rideIdOf(r) === selectedRideId) ? selectedRideId : ""}
                  onChange={(e) => {
                    setSelectedRideId(e.target.value);
                    setConfirmMismatch(false);
                    setServerMismatch(false);
                  }}
                >
                  {classRides.map((r) => {
                    const id = rideIdOf(r);
                    return (
                      <MenuItem key={id} value={id}>
                        {rideLabel(r)}
                        {proposed && proposed.id === id ? ` (${t("reviewQueue.attach.suggested")})` : ""}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t("reviewQueue.orphanSheet.scoreTest")}
                  </Typography>
                  <Typography data-testid="orphan-sheet-score-test">{testLabel(sheetTestId)}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t("reviewQueue.orphanSheet.rideTest")}
                  </Typography>
                  <Typography data-testid="orphan-sheet-ride-test">{selectedRide ? testLabel(targetTestId) : "-"}</Typography>
                </Box>
              </Box>

              {mismatch && (
                <Alert severity="warning" data-testid="orphan-sheet-mismatch">
                  {t(serverMismatch && !clientMismatch ? "reviewQueue.orphanSheet.mismatchServer" : "reviewQueue.orphanSheet.mismatch")}
                  <FormControlLabel
                    sx={{ display: "flex", alignItems: "flex-start", ml: 0, mr: 0, mt: 0.5 }}
                    slotProps={{ typography: { variant: "body2" } }}
                    control={
                      <Checkbox
                        size="small"
                        sx={{ p: 0, mr: 1, mt: "1px" }}
                        checked={confirmMismatch}
                        onChange={(e) => setConfirmMismatch(e.target.checked)}
                      />
                    }
                    label={t("reviewQueue.orphanSheet.mismatchConfirm")}
                  />
                </Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("reviewQueue.attach.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={!selectedRide || (mismatch && !confirmMismatch) || saving}
          onClick={handleAttach}
          data-testid="orphan-sheet-attach-button"
        >
          {saving ? <CircularProgress size={20} /> : t("reviewQueue.orphanSheet.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
